import React, { useEffect, useState, useContext, useRef } from "react";
import { LineChart } from '@mui/x-charts/LineChart';
import { ReviewsContext } from '../context/ReviewsContext';
import { BusinessContext } from '../context/BusinessContext';
import {
  CHART_MARGINS,
  MOBILE_CHART_MARGINS,
  CustomChartTooltip
} from './common/ChartDefaults';
import { useMediaQuery, useTheme } from '@mui/material';
import { processChartData, ChartStateWrapper } from './RatingChartUtils';

// Independent Cumulative Rating Chart component
const CumulativeRatingChart = () => {
  const [chartData, setChartData] = useState({ xData: [], yData: [], yMin: 0, yMax: 5 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { reviewsData, view } = useContext(ReviewsContext);
  const { currencySymbol } = useContext(BusinessContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const chartContainerRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    // Short timeout to ensure DOM is ready and container is properly sized
    const timeoutId = setTimeout(() => {
      if (reviewsData) {
        try {
          const reviews = (reviewsData || []).sort(
            (a, b) => new Date(a.estimated_datetime) - new Date(b.estimated_datetime)
          );
          
          if (reviews.length === 0) {
            setLoading(false);
            return;
          }
          
          const { chartData: newChartData } = processChartData(reviews);
          if (newChartData) {
            setChartData(newChartData);
          } else {
            setError("Could not process review data");
          }
        } catch (error) {
          console.error("Error processing review data for chart:", error);
          setError("Could not process review data");
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    }, 100); // Small delay to ensure container is rendered
    
    return () => clearTimeout(timeoutId);
  }, [reviewsData]);

  const noData = !chartData || !chartData.xData || chartData.xData.length === 0;

  return (
    <ChartStateWrapper loading={loading} error={error} noData={noData}>
      <LineChart
        colors={['#1976d2']} // Fixed direct color value instead of using colors utility
        sx={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          overflow: 'hidden', // Add overflow:hidden to prevent content extending beyond
          // Force SVG elements to have constrained height
          '& svg, & path, & g': {
            maxHeight: '350px !important'
          },
          '.MuiChartsAxis-label': {
            fill: '#546E7A',
            fontWeight: 500,
            fontSize: isSmallScreen ? 10 : 12
          },
          '.MuiChartsAxis-tickLabel': {
            fill: '#546E7A',
            fontSize: isSmallScreen ? 9 : 11
          },
          '.MuiLineElement-series-ratingTrend': {
            strokeWidth: 1.5
          },
          '.MuiAreaElement-series-ratingTrend': {
            fillOpacity: 0.05,
            display: 'block !important', // Force display to override any other styles
            clipPath: 'inset(0 0 0 0)' // Original clip path setting
          },
          // Add better SVG handling
          '& .MuiChart-area': {
            overflow: 'hidden'
          },
          // Ensure the chart container properly clips content
          '& .MuiChartsAxis-root, & .MuiChartsLegend-root': {
            overflow: 'hidden'
          }
        }}
        xAxis={[
          {
            data: chartData.xData,
            scaleType: 'time',
            tickLabelStyle: {
              angle: 45, // Rotates the labels
              textAnchor: 'start', // Aligns them appropriately
              fontSize: isSmallScreen ? 10 : 12, // Smaller font on mobile
            },
            tickLabelInterval: () => true,
            valueFormatter: (value, context) => {
              if (context.location === 'tooltip') {
                return new Date(value).toLocaleDateString(); // Format for tooltip (e.g., MM/DD/YYYY)
              }
            },
          },
        ]}
        yAxis={[
          {
            valueFormatter: (value) => `${value}`,
            tickLabelStyle: {
              fontSize: isSmallScreen ? 9 : 11,
            },
            min: chartData.yMin,
            max: chartData.yMax,
            tickNumber: 3
          },
        ]}
        series={[
          {
            id: 'ratingTrend',
            data: chartData.yData,
            label: 'Rating',
            showMark: false,
            area: true, // Enable area to show fill under the line
            lineStyle: {
              strokeWidth: 2
            },
            // Note: explicitly setting the areaStyle here
            areaStyle: {
              fillOpacity: 0.05,
              display: 'block'
            }
          },
        ]}
        slotProps={{
          legend: { hidden: true },
          tooltip: {
            content: (props) => <CustomChartTooltip {...props} currencySymbol={currencySymbol} view={view} />
          },
          svg: { 
            // Add explicit viewBox and sizing attributes to constrain the SVG
            style: {
              maxHeight: '350px',
              overflow: 'hidden'
            },
            // Set explicit viewBox to constrain the coordinate space
            viewBox: `0 0 ${chartData.xData && chartData.xData.length > 0 ? chartData.xData.length * 10 : 100} 100`
          }
        }}
        margin={isSmallScreen ? 
          {...MOBILE_CHART_MARGINS, left: 40, right:40} : // Increase right margin from 5 to 20px
          CHART_MARGINS
        }
      />
    </ChartStateWrapper>
  );
};

export default CumulativeRatingChart; 